import {
  parseLqe,
  parseLrcLike,
  parseLyl,
  parseLys,
  parseQrc,
  parseSPL,
  parseTTML,
  parseYrc,
  type LyricLine as AmllLyricLine,
} from '@applemusic-like-lyrics/lyric';
import type { LyricLine } from './lyrics';

export type { AmllLyricLine };

// The current official TTML adapter requires Apple's optional line key and
// otherwise silently skips valid TTML paragraphs. Add keys only in the parse
// copy; existing keys (including sidecar translation references) stay intact.
const prepareTtml = (text: string) => {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return text;
  const document = new DOMParser().parseFromString(text, 'application/xml');
  if (document.getElementsByTagName('parsererror').length || document.documentElement?.localName !== 'tt') return text;
  const namespace = 'http://music.apple.com/lyric-ttml-internal';
  const paragraphs = [...document.getElementsByTagNameNS('*', 'p')];
  const keys = new Set(paragraphs.map(line => line.getAttributeNS(namespace, 'key') || line.getAttribute('itunes:key')).filter(Boolean));
  let changed = false;
  for (const [index, line] of paragraphs.entries()) {
    if (line.getAttributeNS(namespace, 'key') || line.getAttribute('itunes:key')) continue;
    let key = `mineradio-line-${index}`;
    while (keys.has(key)) key += '-';
    line.setAttributeNS(namespace, 'itunes:key', key);
    keys.add(key);
    changed = true;
  }
  return changed ? new XMLSerializer().serializeToString(document) : text;
};

const emptyLine = (startTime: number, endTime: number, text: string): AmllLyricLine => ({
  words: [{ startTime, endTime, word: text }],
  translatedLyric: '',
  romanLyric: '',
  startTime,
  endTime,
  isBG: false,
  isDuet: false,
});

const withFiniteEnds = (lines: AmllLyricLine[]) => lines
  .filter((line) => Number.isFinite(line.startTime) && Number.isFinite(line.endTime) && line.endTime >= line.startTime)
  .map((line, index, all) => {
    const nextStart = all[index + 1]?.startTime;
    const endTime = Number.isFinite(nextStart) && nextStart! > line.startTime ? Math.max(line.endTime, nextStart!) : line.endTime;
    return {
      ...line,
      endTime,
      words: line.words.map((word, wordIndex, words) => ({
        ...word,
        endTime: Number.isFinite(word.endTime) && word.endTime > word.startTime
          ? word.endTime
          : words[wordIndex + 1]?.startTime || endTime,
      })),
    };
  });

/**
 * 将现有本地歌词模型转换为 AMLL Core 的歌词模型。
 * 这样即使旧索引只有行级时间轴，也能进入官方 AMLL 渲染器。
 */
export const toAmllLines = (lines: LyricLine[]): AmllLyricLine[] => {
  const sorted = [...lines].sort((a, b) => a.time - b.time);
  return sorted.map((line, index) => {
    const startTime = Math.max(0, Math.round(line.time * 1000));
    const nextStart = sorted[index + 1] ? Math.max(startTime + 300, Math.round(sorted[index + 1].time * 1000)) : startTime + 4000;
    return emptyLine(startTime, nextStart, line.text);
  });
};

/**
 * 使用 AMLL 官方解析器读取 LRC/TTML。SRT 仍走项目已有解析器，再转换为 AMLL 行。
 * 解析失败时返回空数组，由上层保留原有歌词降级显示。
 */
export const parseAmllLyrics = (fileName: string, text: string, fallback: LyricLine[] = []): AmllLyricLine[] => {
  const extension = fileName.toLowerCase().split('.').pop();
  try {
    if (extension === 'lrc') {
      const lines = withFiniteEnds(parseLrcLike(text, { mode: 'spl' }).lines);
      // The upstream parser can return an empty result for valid hour clocks
      // without throwing. Preserve the already parsed line timestamps then.
      return lines.length ? lines : toAmllLines(fallback);
    }
    if (extension === 'ttml') return withFiniteEnds(parseTTML(prepareTtml(text)).lines);
    if (extension === 'spl') return withFiniteEnds(parseSPL(text));
    if (extension === 'yrc') return withFiniteEnds(parseYrc(text));
    if (extension === 'qrc') return withFiniteEnds(parseQrc(text));
    if (extension === 'lys') return withFiniteEnds(parseLys(text));
    if (extension === 'lyl') return withFiniteEnds(parseLyl(text));
    if (extension === 'lqe') return withFiniteEnds(parseLqe(text));
  } catch {
    // 保留项目原有解析器的可恢复降级路径。
  }
  return toAmllLines(fallback);
};
